import React, { Suspense } from 'react';
import lazyWithRetries from '../../middleware/lazyWithRetries';
import Toasty from '../../components/common/Toasty/Toasty';
import OnPageLoadLocationHandler from '../../components/common/LocationServices/OnPageLoadLocationHandler/OnPageLoadLocationHandler';
import StoreSelectionModal from '../../components/common/LocationServices/StoreSelectionModal/StoreSelectionModal';
import SameDayDeliveryModal from '../../components/common/LocationServices/SameDayDeliveryModal/SameDayDeliveryModal';
import ErrorBoundaryWithLogger from '../../components/common/ErrorBoundary/ErrorBoundary';

const Quickview = lazyWithRetries(
  () => import(/* webpackChunkName: "Quickview" */ '../../components/Quickview/Quickview'),
  'Quickview',
);

const Header = lazyWithRetries(
  () => import(/* webpackChunkName: "Header" */ '../../components/templates/Header/Header'),
  'Header',
);

const MicrositeHeader = lazyWithRetries(
  () =>
    import(
      /* webpackChunkName: "MicrositeHeader" */ '../../components/templates/MicrositeHeader/MicrositeHeader'
    ),
  'MicrositeHeader',
);

const HeaderSM = lazyWithRetries(
  () => import(/* webpackChunkName: "HeaderSM" */ '../../components/templates/HeaderSM/HeaderSM'),
  'HeaderSM',
);

const HeaderPLF = lazyWithRetries(
  () =>
    import(/* webpackChunkName: "HeaderPLF" */ '../../components/templates/HeaderPLF/HeaderPLF'),
  'HeaderPLF',
);

const ListsPanel = lazyWithRetries(
  () => import(/* webpackChunkName: "ListsPanel" */ '../../components/ListsPanel/ListsPanel'),
  'ListsPanel',
);

type Props = {
  storeIdentifierName: string;
};

/*
  HeaderRouter handles loading the correct Header template based on the storeIdentifierName.
  Logic or components that are shared among the Header templates should live here.
*/

const HeaderRouter = ({ storeIdentifierName }: Props) => {
  const headerSelector = () => {
    switch (storeIdentifierName) {
      case 'plf':
        return <HeaderPLF />;
      case 'sm':
        return <HeaderSM />;
      case 'pl':
      case 'dsg':
      case 'gg':
      case 'mj':
        return <Header />;
      default:
        return <MicrositeHeader />;
    }
  };

  return (
    <>
      <ErrorBoundaryWithLogger componentId="HeaderRouter">
        <Suspense>{headerSelector()}</Suspense>
      </ErrorBoundaryWithLogger>
      {!['plf', 'sm'].includes(storeIdentifierName) && (
        <>
          {/* Non-Lazy Loaded Components */}
          <ErrorBoundaryWithLogger componentId="HeaderRouter-OnPageLoadLocationHandler">
            <OnPageLoadLocationHandler />
          </ErrorBoundaryWithLogger>
          <ErrorBoundaryWithLogger componentId="HeaderRouter-StoreSelectionModal">
            <StoreSelectionModal />
          </ErrorBoundaryWithLogger>
          {/* Lazy Loaded Components */}
          <ErrorBoundaryWithLogger componentId="HeaderRouter-Quickview">
            <Suspense>
              <Quickview />
            </Suspense>
          </ErrorBoundaryWithLogger>
          <ErrorBoundaryWithLogger componentId="HeaderRouter-SameDayDeliveryModal">
            <Suspense>
              <SameDayDeliveryModal />
            </Suspense>
          </ErrorBoundaryWithLogger>
          <ErrorBoundaryWithLogger componentId="HeaderRouter-ListsPanel">
            <Suspense>
              <ListsPanel />
            </Suspense>
          </ErrorBoundaryWithLogger>
          <ErrorBoundaryWithLogger componentId="HeaderRouter-Toasty">
            <Suspense>
              <Toasty />
            </Suspense>
          </ErrorBoundaryWithLogger>
        </>
      )}
    </>
  );
};

export default HeaderRouter;
